import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * iOS zoom guard — addresses beta feedback that the page becomes too large
 * and the top of the screen becomes unreachable on iOS (Brittny Spaid, Sierra
 * Anderson, Sierra Ayers — all mobile_ios reports).
 *
 * Two complementary behaviors:
 *
 * 1. **Standalone PWA scope** — when the app is launched from the home screen
 *    (`display-mode: standalone`), pinch-zoom is disabled by tightening the
 *    viewport meta. Browser-tab visitors keep full pinch-zoom for WCAG 1.4.4.
 *
 * 2. **Silent auto-reset on route change** — in any context, if the visual
 *    viewport is currently zoomed (>1.05x) when navigating to a new route,
 *    we briefly clamp `maximum-scale=1.0` to force iOS Safari to snap back to
 *    1.0, then restore the original meta on the next tick so users can still
 *    pinch-zoom on individual pages.
 *
 * No on-screen UI. iOS-only side effects (Android does not persist zoom).
 */

const VIEWPORT_META_ID = "viewport";
const STANDALONE_VIEWPORT =
  "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, interactive-widget=resizes-visual";
const RESET_VIEWPORT =
  "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, interactive-widget=resizes-visual";

const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // iPad on iPadOS 13+ reports as Mac; detect via touch points.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1)
  );
};

const isStandalone = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

const getViewportMeta = (): HTMLMetaElement | null => {
  if (typeof document === "undefined") return null;
  return document.querySelector(`meta[name="${VIEWPORT_META_ID}"]`);
};

export const useIOSZoomGuard = (): void => {
  const location = useLocation();

  // 1. Standalone-PWA: lock viewport on mount (one-time).
  useEffect(() => {
    if (!isIOS() || !isStandalone()) return;
    const meta = getViewportMeta();
    if (!meta) return;
    const original = meta.getAttribute("content");
    meta.setAttribute("content", STANDALONE_VIEWPORT);
    return () => {
      if (original) meta.setAttribute("content", original);
    };
  }, []);

  // 2. Route-change auto-reset: only when actually zoomed in browser tab.
  useEffect(() => {
    if (!isIOS()) return;
    if (isStandalone()) return; // standalone already locked above
    const vv = (window as Window & { visualViewport?: VisualViewport }).visualViewport;
    if (!vv || vv.scale <= 1.05) return;

    const meta = getViewportMeta();
    if (!meta) return;
    const original = meta.getAttribute("content");
    if (!original) return;

    meta.setAttribute("content", RESET_VIEWPORT);
    // Restore on next tick so single-page pinch-zoom still works.
    const id = window.setTimeout(() => {
      meta.setAttribute("content", original);
    }, 300);
    return () => window.clearTimeout(id);
  }, [location.pathname]);
};